import { ReactNode, useEffect } from 'react';

import styles from './SignupLayout.module.css';
import { SPLIT_TEST_NAME } from '../../../AnonymousSplitTest/SPLIT_TESTS';
import { useAnonymousSplitTests } from '../../../AnonymousSplitTest/useAnonymousSplitTests';
import { useCurrentStep } from '../../../hooks/useCurrentStep';
import { useLocale } from '../../../hooks/useLocale';
import { useSignupParams } from '../../../hooks/useSignupParams';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import logger from '../../../utils/logger';
import LogoHeading from '../../LogoHeading/LogoHeading';
import MyAccount from '../../MyAccount/MyAccount';
import PoweredByIwoca from '../../PoweredByIwoca/PoweredByIwoca';
import Help from '../../SidePanel/Help/Help';
import SidePanel from '../../SidePanel/SidePanel';
import TrustPilot from '../../TrustPilot/TrustPilot';
import SignupLayoutHeading from '../Navigation/SignupLayoutHeading';
import SignupLayoutHeadingDirectUK from '../Navigation/SignupLayoutHeadingDirectUK';

export const SignupLayout = ({ children }: { children: ReactNode }) => {
  const { isMobile } = useWindowDimensions();
  const currentStep = useCurrentStep();
  const { isDirectCustomer } = useSignupParams();
  const { isUK } = useLocale();

  const hide_top_banner_on_mobile_step_0_split_test = useAnonymousSplitTests({
    testFilter: SPLIT_TEST_NAME.HIDE_TOP_BANNER_ON_MOBILE_STEP_0,
    enabled: isMobile && isUK && currentStep === 'new',
  });
  const included_in_hide_top_banner_on_mobile_step_0_split =
    hide_top_banner_on_mobile_step_0_split_test?.[0]?.branch_name === 'INCLUDE';

  const showSidePanel =
    !(isMobile && currentStep !== 'new' && currentStep !== 'neu') &&
    !included_in_hide_top_banner_on_mobile_step_0_split;

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Reload page if restored by bfcache
  useEffect(() => {
    const handleRefetch = (event: PageTransitionEvent) => {
      if (event.persisted) {
        logger.info('Reloading page after event persisted');
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handleRefetch);

    return () => window.removeEventListener('pageshow', handleRefetch);
  }, []);

  return (
    <div className={styles.container}>
      {isMobile && <LogoHeading />}
      {showSidePanel && <SidePanel />}
      <div className={styles.signupContainer}>
        {isDirectCustomer && isUK && (
          <SignupLayoutHeadingDirectUK currentStep={currentStep} />
        )}
        {!(isDirectCustomer && isUK) && !isMobile && <SignupLayoutHeading />}
        <div className={styles.signupStepContainer}>{children}</div>
      </div>
      {isMobile && (
        <>
          {!isDirectCustomer && isUK && (
            <div className={styles.myAccountContainer}>
              <MyAccount />
            </div>
          )}
          <hr className={styles.footerBreak} />
          <div className={styles.footer}>
            <Help expanded />
            <div>
              <PoweredByIwoca />
              <TrustPilot />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
